import { connect } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { select, showMaximizedChart, hideMaximizedChart } from '../../actions';
import {
  getProjectedDeficitChartData,
  getContributionsChartData,
  getWaterfallChartData,
  getkeyFiguresTableData,
  getInitialDate,
  getInitialAsset,
} from './selectors';
import React from 'react';
import { Modal } from 'react-bootstrap';
import * as d3 from 'd3';
import DeficitChart from '../../charts/DeficitChart';
import WaterfallChart from '../../charts/WaterfallChart';
import KeyFiguresTable from '../../charts/KeyFiguresTable';
import RowChart from '../../charts/RowChart';

const layouts = {
  lg: [
    { i: 'deficit-chart', x: 0, y: 0, w: 6, h: 4, minW: 4, minH: 2 },
    { i: 'row-chart', x: 6, y: 0, w: 6, h: 4, minW: 4, minH: 2 },
    { i: 'waterfall-chart', x: 0, y: 4, w: 6, h: 4, minW: 4, minH: 2 },
    { i: 'key-figures-table', x: 6, y: 4, w: 6, h: 4, minW: 4, minH: 2 },
  ],
  xxs: [
    { i: 'deficit-chart', x: 0, y: 0, w: 1, h: 4, static: true },
    { i: 'row-chart', x: 0, y: 4, w: 1, h: 4, static: true },
    { i: 'waterfall-chart', x: 0, y: 8, w: 1, h: 4, static: true },
    { i: 'key-figures-table', x: 0, y: 12, w: 1, h: 4, static: true },
  ],
};

const ReactResponsiveGridLayout = WidthProvider(Responsive);

const Dashboard = (props) => {
  const charts = {
    deficitChart: <DeficitChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('deficitChart')}
      title={`Projected ${(props.deficit >= 0 ? 'deficit' : 'surplus')} on ${props.formattedDate}: £${d3.formatPrefix(',.0', 1e6)(Math.abs(props.deficit))}`}
      data={props.projectedDeficitChartData}
      onSelected={props.onSelected}
      date={props.date}
      asset={props.asset}
    />,
    rowChart: <RowChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('rowChart')}
      title={`Extra Contributions to remove deficit: £${d3.formatPrefix(',.0', 1e6)(Math.abs(props.extraContributions))}pa`}
      data={props.contributionsChartData}
      onSelected={props.onSelected}
      selected={props.asset}
    />,
    waterfallChart: <WaterfallChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('waterfallChart')}
      title={'Impact of planned contributions and excess returns'}
      data={props.waterfallChartData}
    />,
    keyFiguresTable: <KeyFiguresTable
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('keyFiguresTable')}
      title="Summary"
      data={props.keyFiguresTableData}
    />,
  }

  const modalChart = props.showMaximizedChart ? React.cloneElement(
    charts[props.maximizedChart],
    { onMaximized: null, onMinimized: () => props.onMinimized() },
  ) : null;

  return (
    <div>
      <ReactResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 800, xxs: 0 }}
        cols={{ lg: 12, xxs: 1 }}
        rowHeight={80}
        measureBeforeMount={true}
      >
        <div key="deficit-chart">
          {charts.deficitChart}
        </div>
        <div key="row-chart">
          {charts.rowChart}
        </div>
        <div key="waterfall-chart">
          {charts.waterfallChart}
        </div>
        <div key="key-figures-table">
          {charts.keyFiguresTable}
        </div>
      </ReactResponsiveGridLayout>
      <div className="maximized-chart">
        <Modal show={props.showMaximizedChart} onHide={props.onMinimized} dialogClassName="maximized-chart-modal">
          {modalChart}
        </Modal>
      </div>
    </div>
  );
}

const mapStateToProps = (state, props) => {
  if (props.isLoading) {
    return {
      isLoading: true,
    };
  }

  const fundingForecast = props.data.FundingForecast.data;
  const fundingWaterfall = props.data.FundingWaterfall.data;

  const isDataPresent = state.local.funding_forecast === undefined;

  const date = isDataPresent || state.local.funding_forecast.date === undefined ? getInitialDate(fundingForecast) : state.local.funding_forecast.date;
  const asset = isDataPresent || state.local.funding_forecast.asset === undefined ? getInitialAsset(fundingForecast) : state.local.funding_forecast.asset;

  const projectedDeficitChartData = getProjectedDeficitChartData(fundingForecast);
  const contributionsChartData = getContributionsChartData(fundingForecast, date);
  const waterfallChartData = getWaterfallChartData(fundingWaterfall, date, asset);
  const keyFiguresTableData = getkeyFiguresTableData(fundingForecast, date, asset);

  const deficit = projectedDeficitChartData.find(x => x.asset === asset).values.find(x => d3.timeFormat('%d-%b-%Y')(x.date) === date).deficit;
  const extraContributions = contributionsChartData.find(x => x.id === asset).value;
  const formattedDate = d3.timeFormat('%d %B %Y')(d3.timeParse('%d-%b-%Y')(date));
  const isLoading = false;
  const showMaximizedChart = state.local.showMaximizedChart;
  const maximizedChart = state.local.maximizedChart;

  return {
    isLoading,
    showMaximizedChart,
    maximizedChart,
    date,
    asset,
    projectedDeficitChartData,
    contributionsChartData,
    deficit,
    extraContributions,
    formattedDate,
    waterfallChartData,
    keyFiguresTableData,
  }
}

const mapDispatchToProps = ({
  onSelected: (key, value) => select(key, value, 'funding_forecast'),
  onMaximized: chart => showMaximizedChart(chart),
  onMinimized: hideMaximizedChart,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);



// WEBPACK FOOTER //
// ./src/dashboards/FundingForecast/Dashboard.js